// deps
import React, { ReactElement, memo, useCallback, useMemo, useState } from "react";
import classNames from "classnames";

// interfaces
import { IInAppUpiPaymentGroup } from "@payment-ui/interfaces/InAppUpi";

// helpers
import { isEmpty } from "@swiggy-private/payment-core";
import {
    isPaymentMethodSelected,
    getPaymentGroupDisplayName,
    generatePaymentMethodId,
    isPaymentGroupEnabled,
    getPaymentGroupDisableMessage,
    getPaymentMethods,
    getPaymentGroupIconUrl,
    getPaymentGroupSubtext,
    getPaymentGroupIconPosition,
} from "@payment-ui/helpers/Payment";
import { isNewInAppUpiPaymentMethod } from "@payment-ui/helpers/InAppUpi";

// components
import PaymentGroupContainer from "../components/Container";
import PaymentGroupTitle from "../components/Title";
import NewInAppUpi from "@payment-ui/components/payment-method/new-inapp-upi";
import InAppUpi from "@payment-ui/components/payment-method/inapp-upi";
import ShowMoreCta from "@payment-ui/components/payment-group/components/ShowMoreCta";

// test-ids
import { PAYMENT_GROUP_TEST_IDS } from "@swiggy-private/payment-test-ids";

// styles
import styles from "../PaymentGroup.v4.scss";

// constants
import { TEXTS } from "./constants";

function InAppUpiPaymentGroup(props: IInAppUpiPaymentGroup): ReactElement | null {
    const { paymentData, hideGroupTitle, selectedPaymentId, paymentActions, paymentAmount, isFrictionPage } = props;

    const paymentMethods = useMemo(() => getPaymentMethods(props), [props]);
    const isGroupEnabled = useMemo(() => isPaymentGroupEnabled(paymentData), [paymentData]);
    const [foldSize, setFoldSize] = useState(paymentData?.foldSize || 2);

    const isAddNewUPIPresent = useMemo(() => {
        return paymentMethods?.some(paymentMethod => isNewInAppUpiPaymentMethod(paymentMethod));
    }, [paymentMethods]);

    const inAppUpiMethodsList = useMemo(() => {
        const sliceSize = isAddNewUPIPresent && foldSize + 1 === paymentMethods?.length ? foldSize + 1 : foldSize;
        const methodsList = paymentMethods?.slice(0, sliceSize);

        return methodsList;
    }, [isAddNewUPIPresent, foldSize, paymentMethods]);

    const showMoreCta = useMemo(() => {
        const length = isAddNewUPIPresent ? paymentMethods?.length - 1 : paymentMethods?.length;
        return foldSize < length;
    }, [foldSize, isAddNewUPIPresent, paymentMethods?.length]);

    const expandUpiList = useCallback(() => {
        setFoldSize(paymentMethods?.length);
    }, [paymentMethods]);

    if (isEmpty(paymentMethods)) {
        return null;
    }

    return (
        <PaymentGroupContainer isFrictionPage={isFrictionPage}>
            {!hideGroupTitle && (
                <PaymentGroupTitle
                    title={getPaymentGroupDisplayName(paymentData)}
                    disableMessage={getPaymentGroupDisableMessage(paymentData)}
                    data-testid={PAYMENT_GROUP_TEST_IDS.TITLE.INAPP_UPI}
                    capitalizeTitle={false}
                    groupIcon={getPaymentGroupIconUrl(props.paymentData)}
                    groupIconPosition={getPaymentGroupIconPosition(props.paymentData)}
                    groupSubtext={getPaymentGroupSubtext(props.paymentData)}
                />
            )}
            <div className={styles.paymentGroupContainer}>
                {!isEmpty(inAppUpiMethodsList) && (
                    <div
                        className={classNames(styles.paymentGroupContainerRow, {
                            [styles.paymentGroupContainerWithoutBorder]: !inAppUpiMethodsList.length,
                            [styles.paymentGroupContainerColumn]: true,
                        })}
                    >
                        {inAppUpiMethodsList.map((paymentMethod, index) => {
                            const id = generatePaymentMethodId(paymentMethod);
                            if (isNewInAppUpiPaymentMethod(paymentMethod)) {
                                return (
                                    <NewInAppUpi
                                        key={index}
                                        id={id}
                                        position={index}
                                        isPaymentGroupEnabled={isGroupEnabled}
                                        selected={isPaymentMethodSelected(selectedPaymentId, id)}
                                        methodData={paymentMethod}
                                        paymentActions={paymentActions}
                                    />
                                );
                            }

                            return (
                                <InAppUpi
                                    key={index}
                                    id={id}
                                    position={index}
                                    selected={isPaymentMethodSelected(selectedPaymentId, id)}
                                    methodData={paymentMethod}
                                    isPaymentGroupEnabled={isGroupEnabled}
                                    paymentActions={paymentActions}
                                    paymentAmount={paymentAmount}
                                />
                            );
                        })}
                    </div>
                )}

                {showMoreCta && (
                    <ShowMoreCta
                        handleClick={expandUpiList}
                        dataTestId={PAYMENT_GROUP_TEST_IDS.INAPP_UPI.MORE_METHODS_CTA}
                        text={TEXTS.SHOW_MORE_CTA}
                        icon={true}
                    />
                )}
            </div>
        </PaymentGroupContainer>
    );
}

export default memo(InAppUpiPaymentGroup);
